import {
  Alert,
  Box,
  Button,
  Container,
  Flex,
  Progress,
  rem,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { Resolution, usePDF } from "react-to-pdf";

import { MainLayout } from "../../layouts/MainLayout";
import { CareerMilestones } from "../../widgets/report/browser/CareerMilestones";
import { CareerInNumbers } from "../../widgets/report/browser/CareerInNumbers";
import { StrengthsDerivedFromResume } from "../../widgets/report/browser/StrengthsDerivedFromResume";
import { TopTalents } from "../../widgets/report/browser/TopTalents";
import { TrendingSkills } from "../../widgets/report/browser/TrendingSkills";
import { SkillsPhasingOut } from "../../widgets/report/browser/SkillsPhasingOut";
import { OptimalPositions } from "../../widgets/report/browser/OptimalPositions";
import { HigherPositions } from "../../widgets/report/browser/HigherPositions";
import { RolesToConsider } from "../../widgets/report/browser/RolesToConsider";
import { FutureSkills } from "../../widgets/report/browser/FutureSkills";
import { DataAnalysisSkills } from "../../widgets/report/browser/DataAnalysisSkills";
import CloudOfProfessions from "../../widgets/report/browser/CloudOfProfessions";
import { SkillsAssessmentComponent } from "../../widgets/report/browser/SkillsAssessment";
import { EssentialSkillCourses } from "../../widgets/report/browser/EssentialSkillCourses";

import { PDFTitle } from "../../widgets/report/pdf/PDFTitle";
import { TitlePage } from "../../widgets/report/pdf/TitlePage";
import { CareerMilestonesPDF } from "../../widgets/report/pdf/CareerMilestonesPDF";
import { CareerInNumbersPDF } from "../../widgets/report/pdf/CareerInNumbersPDF";
import { StrengthsDerivedFromResumePDF } from "../../widgets/report/pdf/StrengthsDerivedFromResumePDF";
import { TopTalentsPDF } from "../../widgets/report/pdf/TopTalentsPDF";
import { TrendingSkillsPDF } from "../../widgets/report/pdf/TrendingSkillsPDF";
import { SkillsPhasingOutPDF } from "../../widgets/report/pdf/SkillsPhasingOutPDF";
import { OptimalPositionsPDF } from "../../widgets/report/pdf/OptimalPositionsPDF";
import { HigherPositionsPDF } from "../../widgets/report/pdf/HigherPositionsPDF";
import { RolesToConsiderPDF } from "../../widgets/report/pdf/RolesToConsiderPDF";
import { FutureSkillsPDF } from "../../widgets/report/pdf/FutureSkillsPDF";
import { DataAnalysisSkillsPDF } from "../../widgets/report/pdf/DataAnalysisSkillsPDF";
import { SkillsAssessmentPDF } from "../../widgets/report/pdf/SkillsAssessmentPDF";
import { EssentialSkillCoursesPDF } from "../../widgets/report/pdf/EssentialSkillCoursesPDF";
import { useTranslation } from "react-i18next";
import CloudOfProfessionsPDF from "../../widgets/report/pdf/CloudOfProfessionsPDF";

import useReportStore from "../../store/useReportStore";
import useAuthStore from "../../store/useAuthStore";

import useProfileStore from "../../store/useProfileStore";
import { useNavigate } from "react-router";
import { useReportFlow } from "./useReportFlow";
import { ScrollToTop } from "../../widgets/report/utils/ScrollToTop";
import { ReportSelector } from "../../widgets/report/utils/ReportSelector";
import {
  OnboardingTourFocusRevealProps,
  OnboardingTourStep,
} from "@gfazioli/mantine-onboarding-tour";
import { scrollToTop } from "../../utils/helpers";
import { useOnboardingReportSteps } from "../../utils/onboardingTourSteps";
import { ReusableOnboardingTour } from "../../utils/components/onboardingTour/OnboardingTourWrapper";
import { TourButton } from "../../utils/components/onboardingTour/TourButton";
import UserPrice from "../../widgets/report/utils/UserPrice";
import { processUserPrice } from "../../api/reportApi";

export const Report = () => {
  const matches = useMediaQuery("(max-width: 1100px)");
  const mobile = useMediaQuery("(max-width: 650px)");
  const { toPDF, targetRef } = usePDF({
    filename: "report.pdf",
    resolution: Resolution.LOW,
  });
  const [isPDF, setIsPDF] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    reportLoading,
    report,
    progress,
    error,
    reportProcessing,
    fetchAllReports,
    reportId,
    makeReport,
    getUserPrice,
    userMarketPrice,
  } = useReportStore();

  const [started, { open, close }] = useDisclosure(false);

  const focusRevealProps: OnboardingTourFocusRevealProps = {
    popoverProps: {
      arrowSize: 20,
      position: mobile ? "bottom" : "left",
    },
  };

  const onboardingSteps: OnboardingTourStep[] = useOnboardingReportSteps();

  const { cvProcessing, user } = useAuthStore();

  const {
    changes,
    profileLanguage,
    fetchProfile,
    profile,
    setChanges,
    tourButtonShow,
    setUserMarketChanges,
  } = useProfileStore();
  const [generating, setGenerating] = useState(false);

  const [showReportNotice, setShowReportNotice] = useState(false);
  const theme = useMantineTheme();

  useEffect(() => {
    getUserPrice(Object.keys(profileLanguage!)[0]);
  }, [reportProcessing, profileLanguage]);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (changes && user?.isLoggedIn) {
      setShowReportNotice(true);
    }
  }, []);

  useEffect(() => {
    if (!reportId && Object.keys(profileLanguage!)[0] !== "en") {
      fetchAllReports("en");
    } else {
      if (reportProcessing !== "processing")
        fetchAllReports(Object.keys(profileLanguage!)[0]);
    }
  }, []);

  useEffect(() => {
    if (reportProcessing !== "processing")
      fetchAllReports(Object.keys(profileLanguage!)[0]);
  }, [profileLanguage, reportProcessing]);

  const { isProcessing, isTranslating, translationProgress } = useReportFlow();

  const isIOSSafari = () => {
    const ua = window.navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const isSafari =
      isIOS &&
      !!navigator.vendor &&
      navigator.vendor.includes("Apple") &&
      !ua.includes("CriOS") &&
      !ua.includes("FxiOS");
    return isSafari;
  };

  const handleGeneratePDF = () => {
    setGenerating(true);
    document.body.style.overflow = "hidden";
    setIsPDF(true);

    if (isIOSSafari()) {
      alert(t("pdf.ios_safari_notice"));
    }

    setTimeout(() => {
      toPDF();
      setIsPDF(false);
    }, 4000);

    setTimeout(() => {
      setGenerating(false);
      document.body.style.overflow = "";
    }, 8000);
  };

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleMakeReport = async () => {
    setErrorMessage("");
    setLoading(true);
    try {
      const res = await makeReport();
      if (res) {
        setChanges(false);
        setUserMarketChanges(false);
        processUserPrice();
      } else {
        setErrorMessage(t("no_cv"));
      }
    } catch (error) {
      console.log(error);
      setErrorMessage(t("no_cv"));
    }
    setLoading(false);
  };

  if (
    isProcessing ||
    reportLoading ||
    !report ||
    cvProcessing === "processing" ||
    isTranslating ||
    error === "not_found"
  ) {
    return (
      <MainLayout>
        <Container size="xs" style={{ textAlign: "center", paddingTop: 80 }}>
          <Stack gap="34px">
            {isTranslating ? (
              <>
                <Title order={3}>{t("your_report_is_being_translated")}</Title>
                <Progress
                  value={translationProgress}
                  size="xl"
                  color="purple"
                  transitionDuration={300}
                />
              </>
            ) : cvProcessing === "processing" ? (
              <>
                <Title order={3}>{t("your_cv_is_being_processed")}</Title>
                <Progress
                  value={100}
                  size="xl"
                  color="purple"
                  animated
                  striped
                />
              </>
            ) : isProcessing ? (
              <>
                <Title order={3}>{t("your_data_is_being_analyzed")}</Title>
                <Progress
                  value={progress}
                  size="xl"
                  color="purple"
                  transitionDuration={300}
                />
              </>
            ) : cvProcessing === "failed" ? (
              <>
                <Title order={3}>{t("cv_processing_failed")}</Title>
                {errorMessage && (
                  <Text color="#B00020" size="sm" mt={10}>
                    {errorMessage}
                  </Text>
                )}
                <Flex
                  direction={mobile ? "column" : "row"}
                  align="center"
                  justify="center"
                  gap={20}
                >
                  <Button
                    variant="outline"
                    onClick={() => navigate("/profile")}
                  >
                    {t("go_to_upload")}
                  </Button>
                </Flex>
              </>
            ) : error === "not_found" ? (
              <>
                {profile?.profile_completeness.filled_fields.cv &&
                profile?.profile_completeness.filled_fields.survey ? (
                  <>
                    <Title order={3}>{t("no_report_generate")}</Title>
                    {errorMessage && (
                      <Text color="#B00020" size="sm" mt={10}>
                        {errorMessage}
                      </Text>
                    )}
                    <Flex
                      direction={mobile ? "column" : "row"}
                      align="center"
                      justify="center"
                      gap={20}
                    >
                      <Button
                        variant="outline"
                        onClick={() => navigate("/profile")}
                      >
                        {t("go_to_upload")}
                      </Button>
                      <Button onClick={handleMakeReport} loading={loading}>
                        {t("generate_report")}
                      </Button>
                    </Flex>
                  </>
                ) : (
                  <>
                    <Title order={3}>{t("no_report")}</Title>
                    <Flex
                      direction={mobile ? "column" : "row"}
                      align="center"
                      justify="center"
                      gap={20}
                    >
                      <Button
                        variant="outline"
                        onClick={() => navigate("/profile/edit/goals")}
                      >
                        {t("go_to_edit")}
                      </Button>
                      <Button onClick={() => navigate("/profile")}>
                        {t("go_to_upload")}
                      </Button>
                    </Flex>
                  </>
                )}
              </>
            ) : (
              <>
                <Title order={3}>{t("loading_your_report")}</Title>
                <Progress
                  value={100}
                  size="xl"
                  color="purple"
                  animated
                  striped
                />
              </>
            )}
          </Stack>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {generating && (
        <Box px={matches ? 16 : 40} py={matches ? 24 : 32} h="100vh">
          <Flex
            align={matches ? "start" : "center"}
            gap={16}
            justify="space-between"
            direction={matches ? "column" : undefined}
          >
            <Title size={matches ? 36 : 48}>{t("career_journey_title")}</Title>
            <Button
              miw="max-content"
              variant="outline"
              onClick={handleGeneratePDF}
              className="download-button"
              disabled
            >
              {t("download_pdf")}
            </Button>
          </Flex>
          <Container size="xs" style={{ textAlign: "center", paddingTop: 80 }}>
            <Title order={2}>{t("generating_report_pdf")}</Title>
            <Progress
              mt="md"
              value={100}
              size="xl"
              color="purple"
              animated
              striped
            />
          </Container>
        </Box>
      )}
      {isPDF ? (
        <>
          <Box w="1191px" m="auto" ref={targetRef}>
            <PDFTitle />
            <TitlePage title={t("part_1_title")} />
            <CloudOfProfessionsPDF />
            <CareerMilestonesPDF />
            <CareerInNumbersPDF />
            <StrengthsDerivedFromResumePDF />
            <TitlePage title={t("part_2_title")} />
            <TopTalentsPDF />
            <TrendingSkillsPDF />
            <SkillsPhasingOutPDF />
            <TitlePage title={t("part_3_title")} />
            <OptimalPositionsPDF />
            <HigherPositionsPDF />
            <RolesToConsiderPDF />
            <TitlePage title={t("part_4_title")} />
            <FutureSkillsPDF />
            <DataAnalysisSkillsPDF />
            <SkillsAssessmentPDF />
            <EssentialSkillCoursesPDF />
          </Box>
        </>
      ) : (
        <Box px={matches ? 16 : 40} py={matches ? 24 : 32}>
          <ReusableOnboardingTour
            focusRevealProps={focusRevealProps}
            tour={onboardingSteps}
            started={!profile?.display_preferences?.hide_tour_tooltip_report}
            onOnboardingTourEnd={() => {
              tourButtonShow({ hide_tour_tooltip_report: true });
              close();
              scrollToTop();
            }}
            maw={mobile ? 330 : 260}
            __size="xs"
          >
            <Flex
              align={matches ? "start" : "center"}
              gap={16}
              justify="space-between"
              direction={matches ? "column" : undefined}
            >
              <Title size={matches ? 36 : 48}>
                {t("career_journey_title")}
              </Title>

              <Button
                data-onboarding-tour-id="button-download-pdf"
                miw="max-content"
                variant="outline"
                onClick={handleGeneratePDF}
                className="download-button"
              >
                {t("download_pdf")}
              </Button>
            </Flex>
            {!generating && (
              <TourButton
                onClick={() => {
                  tourButtonShow({ hide_tour_tooltip_report: false });
                  open();
                }}
              />
            )}
            <UserPrice />
            <ReportSelector />
            <CloudOfProfessions />
            <CareerMilestones />
            <CareerInNumbers />
            <StrengthsDerivedFromResume />
            <Title size={matches ? 28 : 48} mt={32}>
              {t("analyzing_skills_title")}
            </Title>
            <TopTalents />
            <TrendingSkills />
            <SkillsPhasingOut />
            <Title size={matches ? 28 : 48} mt={32}>
              {t("ideal_roles_title")}
            </Title>
            <OptimalPositions />
            <HigherPositions />
            <RolesToConsider />
            <Title size={matches ? 28 : 48} mt={32}>
              {t("personalized_roadmap_title")}
            </Title>
            <FutureSkills />
            <DataAnalysisSkills />
            <SkillsAssessmentComponent />
            <EssentialSkillCourses />
          </ReusableOnboardingTour>
        </Box>
      )}

      <Flex align="center" justify="center" p={20}>
        <ScrollToTop />
        {showReportNotice && !started && (
          <Alert
            title={t("outdated_report_title")}
            color="violet"
            withCloseButton
            variant="filled"
            onClose={() => setShowReportNotice(false)}
            style={{
              position: "fixed",
              bottom: rem(30),
              left: rem(30),
              zIndex: 4,
              width: rem(360),
              maxWidth: "90%",
              boxShadow: theme.shadows.lg,
            }}
          >
            <Text size="sm" mb="lg">
              {t("outdated_report_message")}
            </Text>
            <Flex justify="space-between">
              <div />
              <Button
                size="xs"
                onClick={() => {
                  navigate("/profile/edit/contacts");
                  setShowReportNotice(false);
                }}
              >
                {t("go_edit")}
              </Button>
            </Flex>
          </Alert>
        )}
      </Flex>
    </MainLayout>
  );
};
